import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface Chip {
  id: string;
  name: string;
  slug: string;
  type: "knowledge" | "directive";
  content: string;
  description: string;
  emphasis: "low" | "medium" | "high";
  author: "justin" | "agent";
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChipVersion {
  id: string;
  chip_id: string;
  content: string;
  description: string;
  emphasis: string;
  edited_by: string;
  created_at: string;
}

export function useChips() {
  return useQuery<{ items: Chip[] }>({
    queryKey: ["chips"],
    queryFn: () => apiFetch<{ items: Chip[] }>("/api/dev/chips"),
    refetchInterval: 30_000,
  });
}

export function useChipDetail(id: string | null) {
  return useQuery<{ chip: Chip; versions: ChipVersion[] }>({
    queryKey: ["chip-detail", id],
    queryFn: () => apiFetch<{ chip: Chip; versions: ChipVersion[] }>(`/api/dev/chips/${id}`),
    enabled: !!id,
  });
}

export function useAgentChips(agent: string) {
  return useQuery<{ items: Chip[] }>({
    queryKey: ["agent-chips", agent],
    queryFn: () => apiFetch<{ items: Chip[] }>(`/api/dev/agents/${agent}/chips`),
    refetchInterval: 30_000,
  });
}

export function useSaveAgentChips() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agent, chipIds }: { agent: string; chipIds: string[] }) =>
      apiFetch<{ ok: boolean; items: Chip[] }>(`/api/dev/agents/${agent}/chips`, {
        method: "PUT",
        body: JSON.stringify({ chipIds }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["agent-chips", variables.agent] });
    },
  });
}

export function useCreateChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: {
      name: string;
      slug: string;
      type: "knowledge" | "directive";
      content: string;
      description: string;
      emphasis?: "low" | "medium" | "high";
    }) =>
      apiFetch<{ ok: boolean; chip: Chip }>("/api/dev/chips", {
        method: "POST",
        body: JSON.stringify(fields),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chips"] });
    },
  });
}

export function useUpdateChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Pick<Chip, "name" | "content" | "description" | "emphasis">> }) =>
      apiFetch<{ ok: boolean; chip: Chip }>(`/api/dev/chips/${id}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chips"] });
      queryClient.invalidateQueries({ queryKey: ["chip-detail"] });
    },
  });
}

export function useDeleteChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/dev/chips/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chips"] });
      queryClient.invalidateQueries({ queryKey: ["agent-chips"] });
    },
  });
}
